import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/Toast';
import ConfirmationModal from '../../components/ConfirmationModal';
import Spinner from '../../components/Spinner';
import './AdminProducts.css';

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [syncModal, setSyncModal] = useState(null);
  const navigate = useNavigate();
  const { toast, showToast, closeToast } = useToast();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [products, searchTerm, filterStatus, filterVisibility, filterCategory]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get('/api/admin/products', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/login');
      } else {
        setLoading(false);
      }
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data);
    } catch (error) {
      // Failed to load categories - not critical
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(term) ||
        p.sku?.toLowerCase().includes(term) ||
        p.category?.toLowerCase().includes(term)
      );
    }

    // Published status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => {
        if (filterStatus === 'published') return p.published === 1;
        if (filterStatus === 'unpublished') return p.published === 0;
        if (filterStatus === 'featured') return p.featured === 1;
        return true;
      });
    }

    // Visibility filter
    if (filterVisibility === 'no-images') {
      filtered = filtered.filter(p => !p.images || p.images.length === 0);
    } else if (filterVisibility !== 'all') {
      filtered = filtered.filter(p => p.visibility === filterVisibility);
    }

    // Category filter
    if (filterCategory === 'uncategorized') {
      filtered = filtered.filter(p => !p.category || p.category.trim() === '');
    } else if (filterCategory !== 'all') {
      filtered = filtered.filter(p => p.category?.includes(filterCategory));
    }

    setFilteredProducts(filtered);
  };

  const togglePublished = async (productId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const product = products.find(p => p.id === productId);

      await axios.put(`/api/admin/products/${productId}`, {
        ...product,
        published: currentStatus ? 0 : 1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchProducts();
      showToast('Product updated successfully', 'success');
    } catch (error) {
      showToast('Failed to update product: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const toggleFeatured = async (productId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const product = products.find(p => p.id === productId);

      await axios.put(`/api/admin/products/${productId}`, {
        ...product,
        featured: currentStatus ? 0 : 1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchProducts();
      showToast(`Product ${currentStatus ? 'removed from featured' : 'marked as featured'}`, 'success');
    } catch (error) {
      showToast('Failed to update featured status: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const confirmDeleteProduct = (productId, productName) => {
    setDeleteConfirm({ productId, productName });
  };

  const deleteProduct = async () => {
    if (!deleteConfirm) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/admin/products/${deleteConfirm.productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDeleteConfirm(null);
      fetchProducts();
      showToast('Product deleted successfully', 'success');
    } catch (error) {
      showToast('Failed to delete product: ' + (error.response?.data?.error || error.message), 'error');
      setDeleteConfirm(null);
    }
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const toggleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const executeBulkAction = async (action) => {
    if (selectedProducts.size === 0) return;
    const token = localStorage.getItem('adminToken');

    try {
      if (action === 'publish' || action === 'unpublish') {
        const published = action === 'publish' ? 1 : 0;
        const visibility = action === 'publish' ? 'visible' : 'hidden';

        await axios.put('/api/admin/products/bulk-visibility', {
          ids: [...selectedProducts],
          published,
          visibility
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put('/api/admin/products/bulk-featured', {
          ids: [...selectedProducts],
          featured: action === 'feature' ? 1 : 0
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      const actionLabels = {
        publish: 'published',
        unpublish: 'unpublished',
        feature: 'marked as featured',
        unfeature: 'removed from featured'
      };

      showToast(`${selectedProducts.size} products ${actionLabels[action]}`, 'success');
      setSelectedProducts(new Set());
      fetchProducts();
    } catch (error) {
      showToast('Bulk action failed: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleBulkSync = () => {
    setSyncModal({
      title: 'Bulk Sync from Printify',
      message: (
        <div>
          <p>This will re-sync <strong>all Printify-linked products</strong> to pull the latest data, including any newly generated images.</p>
          <div className="info-message" style={{marginTop: '8px'}}>
            <strong>This process runs in the background:</strong>
            <ul>
              <li>Products will be updated one at a time</li>
              <li>Your categories, featured status, and visibility will be preserved</li>
              <li>Products deleted from Printify will be flagged</li>
              <li>Check the logs page for progress</li>
            </ul>
          </div>
        </div>
      ),
      onConfirm: async () => {
        setSyncModal(null);
        setBulkSyncing(true);
        try {
          const token = localStorage.getItem('adminToken');
          const response = await axios.post('/api/webhooks/bulk-sync-from-printify', {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          showToast(`Bulk sync started for ${response.data.totalProducts} products. Check logs for progress.`, 'success');
        } catch (error) {
          showToast('Bulk sync failed: ' + (error.response?.data?.error || error.message), 'error');
        } finally {
          setBulkSyncing(false);
        }
      }
    });
  };

  if (loading) return <Spinner text="Loading products..." />;

  return (
    <div className="admin-products">
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      <div className="admin-header">
        <div className="admin-header-left">
          <img src="/images/wtf_wear-1.png" alt="WTF Wear" className="admin-logo" />
          <h1>Manage Products</h1>
        </div>
        <div className="admin-nav">
          <button onClick={() => navigate('/')} className="btn-secondary">
            ← Back to Store
          </button>
          <button onClick={() => navigate('/admin/dashboard')} className="btn-secondary">
            Dashboard
          </button>
          <button onClick={() => navigate('/admin/orders')} className="btn-secondary">
            Orders
          </button>
          <button onClick={() => {
            localStorage.removeItem('adminToken');
            navigate('/');
          }} className="btn-danger">
            Logout
          </button>
        </div>
      </div>

      <div className="products-info-banner">
        <i className="fas fa-info-circle"></i>
        <span>
          All products sync automatically from Printify. New products appear as <strong>unpublished</strong> until
          you publish them. Use the checkboxes and "Publish Selected" to make products available on the store.
          Categories, featured status, and sale prices are managed here and won't be overwritten by Printify.
        </span>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search by name, SKU, or category..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="filters-row">
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="unpublished">Unpublished</option>
            <option value="featured">Featured</option>
          </select>

          <select
            className="filter-select"
            value={filterVisibility}
            onChange={(e) => setFilterVisibility(e.target.value)}
          >
            <option value="all">All Visibility</option>
            <option value="visible">Visible</option>
            <option value="hidden">Hidden</option>
            <option value="no-images">Missing Images</option>
          </select>

          <select
            className="filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="uncategorized">Uncategorized</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="results-count">
          Showing {filteredProducts.length} of {products.length} products
          <button
            className="btn-bulk btn-sync-all"
            onClick={handleBulkSync}
            disabled={bulkSyncing}
            title="Re-sync all products from Printify"
          >
            {bulkSyncing ? 'Syncing...' : 'Sync All from Printify'}
          </button>
          {selectedProducts.size > 0 && (
            <span className="bulk-actions">
              {' '} | {selectedProducts.size} selected:
              <button className="btn-bulk btn-publish" onClick={() => executeBulkAction('publish')}>
                Publish Selected
              </button>
              <button className="btn-bulk btn-unpublish" onClick={() => executeBulkAction('unpublish')}>
                Unpublish Selected
              </button>
              <button className="btn-bulk btn-feature" onClick={() => executeBulkAction('feature')}>
                Feature Selected
              </button>
              <button className="btn-bulk btn-unfeature" onClick={() => executeBulkAction('unfeature')}>
                Unfeature Selected
              </button>
            </span>
          )}
        </div>
      </div>

      <div className="products-table-container">
        <table className="products-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={filteredProducts.length > 0 && selectedProducts.size === filteredProducts.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Image</th>
              <th>Name</th>
              <th>SKU</th>
              <th>Price</th>
              <th>Category</th>
              <th>Status</th>
              <th>Sync</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map(product => (
              <tr key={product.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedProducts.has(product.id)}
                    onChange={() => toggleSelectProduct(product.id)}
                  />
                </td>
                <td>
                  {product.images && product.images[0] && (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="product-thumbnail"
                    />
                  )}
                </td>
                <td>
                  <div className="product-name">{product.name}</div>
                  <div className="product-meta">{product.variation_count} variations</div>
                </td>
                <td className="sku-cell">{product.sku || '-'}</td>
                <td className="price-cell">
                  {product.sale_price ? (
                    <>
                      <span className="sale-price">${product.sale_price}</span>
                      <span className="regular-price">${product.regular_price}</span>
                    </>
                  ) : (
                    <span>${product.regular_price}</span>
                  )}
                </td>
                <td className="category-cell">{product.category || '-'}</td>
                <td>
                  <div className="status-badges">
                    <span className={`badge ${product.published ? 'badge-success' : 'badge-warning'}`}>
                      {product.published ? 'Published' : 'Draft'}
                    </span>
                    {product.visibility === 'hidden' && (
                      <span className="badge badge-danger">Hidden</span>
                    )}
                    {product.featured === 1 && (
                      <span className="badge badge-info">Featured</span>
                    )}
                  </div>
                </td>
                <td>
                  {product.printify_product_id ? (
                    <span className="sync-badge sync-yes" title="Synced with Printify">
                      Printify
                    </span>
                  ) : (
                    <span className="sync-badge sync-no" title="Local only">
                      Local
                    </span>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-action btn-edit"
                      onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                    >
                      Edit
                    </button>
                    <button
                      className={`btn-action ${product.published ? 'btn-unpublish' : 'btn-publish'}`}
                      onClick={() => togglePublished(product.id, product.published)}
                    >
                      {product.published ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      className={`btn-action ${product.featured ? 'btn-unfeature' : 'btn-feature'}`}
                      onClick={() => toggleFeatured(product.id, product.featured)}
                    >
                      {product.featured ? 'Unfeature' : 'Feature'}
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={() => confirmDeleteProduct(product.id, product.name)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredProducts.length === 0 && (
          <div className="no-results">
            No products found matching your filters.
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Product"
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={deleteProduct}>
              Delete
            </button>
          </>
        }
      >
        <p>Are you sure you want to delete "{deleteConfirm?.productName}"?</p>
        <p><strong>This action cannot be undone.</strong></p>
      </ConfirmationModal>

      {/* Bulk Sync Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!syncModal}
        onClose={() => setSyncModal(null)}
        title={syncModal?.title || 'Sync'}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setSyncModal(null)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={syncModal?.onConfirm}>
              Start Sync
            </button>
          </>
        }
      >
        {syncModal?.message}
      </ConfirmationModal>
    </div>
  );
}

export default AdminProducts;
